import React, { useEffect, useState } from "react";
import { onValue, ref, set, push } from "firebase/database";
import { database } from "../../../../utils/firebase";
import { useNavigate, useParams } from "react-router-dom";
import { TYPE_MAP, TIE_BEHAVIOR_MAP } from "../../../../utils/constants";
import {Breadcrumbs, Button, Dropdown, Link, Menu, MenuButton, MenuItem, Select, Typography, Option} from "@mui/joy";
import QuickReportGenerator from "../../QuickReportGenerator/QuickReportGenerator";
import PrintIcon from "@mui/icons-material/Print";
import dayjs from "dayjs";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DateTimePicker} from "@mui/x-date-pickers/DateTimePicker";

const Payout = () => {
    const { leagueId, payoutId } = useParams();
    const navigate = useNavigate();
    const [payoutData, setPayoutData] = useState({});
    const [payoutName, setPayoutName] = useState('');
    const [isSaved, setIsSaved] = useState(false);
    const [isEdited, setIsEdited] = useState(false);
    const [payoutOrder, setPayoutOrder] = useState([]);
    const [otherPayouts, setOtherPayouts] = useState([]);
    const [leagueData, setLeagueData] = useState({});

    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs());

    const payoutRef = ref(database, `leagues/${leagueId}/payouts/${payoutId}`);

    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [reportType, setReportType] = useState('');

    useEffect(() => {
        const unsubscribe = onValue(payoutRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                setPayoutData(data);
                setPayoutName(data.name || '');
                setStartDate(dayjs((data.startDate || Date.now() / 1000) * 1000));
                setEndDate(dayjs((data.endDate || Date.now() / 1000) * 1000));
                initializePayoutOrder(data);
                setOtherPayouts(data.otherPayouts ? Object.values(data.otherPayouts) : []); // Convert to array
            }
        });
        return () => unsubscribe();
    }, [leagueId, payoutId]);

    useEffect(() => {
        const leagueRef = ref(database, `leagues/${leagueId}`);
        const unsubscribe = onValue(leagueRef, (snapshot) => {
            const data = snapshot.val();
            setLeagueData(data);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isEdited) {
                event.preventDefault();
                event.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isEdited]);

    const initializePayoutOrder = (data) => {
        const newMainPayouts = { ...data.mainPayouts };
        const newPayoutOrder = [];

        Object.keys(newMainPayouts).forEach(groupKey => {
            const payoutsArray = Object.entries(newMainPayouts[groupKey].payouts || {})
                .map(([key, value]) => ({ key, ...value }))
                .filter(p => p.priority !== undefined)
                .sort((a, b) => a.priority - b.priority);

            payoutsArray.forEach((payout, index) => {
                newMainPayouts[groupKey].payouts[payout.key].priority = index;
                newPayoutOrder.push(payout.key);
            });
        });

        setPayoutData(prevData => ({ ...prevData, mainPayouts: newMainPayouts }));
        setPayoutOrder(newPayoutOrder);
    };

    const handleInputChange = (groupKey, payoutKey, field, value) => {
        setPayoutData(prevData => {
            const newMainPayouts = { ...prevData.mainPayouts };
            newMainPayouts[groupKey].payouts[payoutKey] = {
                ...newMainPayouts[groupKey].payouts[payoutKey],
                [field]: field === 'amount' ? parseFloat(value).toFixed(2) : value
            };
            return { ...prevData, mainPayouts: newMainPayouts };
        });
        setIsEdited(true);
    };

    const handleAmountBlur = (groupKey, payoutKey, value) => {
        setPayoutData(prevData => {
            const newMainPayouts = { ...prevData.mainPayouts };
            newMainPayouts[groupKey].payouts[payoutKey].amount = parseFloat(value).toFixed(2);
            return { ...prevData, mainPayouts: newMainPayouts };
        });
        setIsEdited(true);
    };


    const handleGroupTitleChange = (groupKey, value) => {
        setPayoutData(prevData => {
            const newMainPayouts = { ...prevData.mainPayouts };
            newMainPayouts[groupKey].name = value;
            return { ...prevData, mainPayouts: newMainPayouts };
        });
        setIsEdited(true);
    };

    const movePayout = (index, direction) => {
        setPayoutOrder(prevOrder => {
            const newOrder = [...prevOrder];
            const newIndex = index + direction;
            if (newIndex < 0 || newIndex >= newOrder.length) return prevOrder;

            [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
            return newOrder;
        });
        setIsEdited(true);
    };

    const addPayout = (groupKey) => {
        const newPayoutRef = push(ref(database, `leagues/${leagueId}/payouts/${payoutId}/mainPayouts/${groupKey}/payouts`));
        const newPayoutKey = newPayoutRef.key;

        setPayoutData(prevData => {
            const newMainPayouts = { ...prevData.mainPayouts };
            if (!newMainPayouts[groupKey].payouts) {
                newMainPayouts[groupKey].payouts = {};
            }
            const newPayout = {
                name: '',
                amount: 0,
                type: 'net',
                tieBehavior: 'split',
                priority: Object.keys(newMainPayouts[groupKey].payouts).length
            };
            newMainPayouts[groupKey].payouts[newPayoutKey] = newPayout;
            return { ...prevData, mainPayouts: newMainPayouts };
        });

        setPayoutOrder(prevOrder => [...prevOrder, newPayoutKey]);
        setIsEdited(true);
    };

    const deletePayout = (groupKey, payoutKey) => {
        setPayoutData(prevData => {
            const newMainPayouts = { ...prevData.mainPayouts };
            delete newMainPayouts[groupKey].payouts[payoutKey];
            return { ...prevData, mainPayouts: newMainPayouts };
        });
        setPayoutOrder(prevOrder => prevOrder.filter(key => key !== payoutKey));
        setIsEdited(true);
    };

    const addPayoutGroup = () => {
        const newGroupKey = push(ref(database, `leagues/${leagueId}/payouts/${payoutId}/mainPayouts`)).key;

        setPayoutData(prevData => {
            const newMainPayouts = { ...prevData.mainPayouts };
            newMainPayouts[newGroupKey] = {
                name: 'New Group',
                payouts: {}
            };
            return { ...prevData, mainPayouts: newMainPayouts };
        });
        setIsEdited(true);
    };

    const deletePayoutGroup = (groupKey) => {
        setPayoutData(prevData => {
            const newMainPayouts = { ...prevData.mainPayouts };
            delete newMainPayouts[groupKey];
            return { ...prevData, mainPayouts: newMainPayouts };
        });
        setIsEdited(true);
    };

    const addOtherPayout = () => {
        setOtherPayouts(prevPayouts => [
            ...prevPayouts,
            { name: '', amount: 0, holeBound: false }
        ]);
        setIsEdited(true);
    };

    const deleteOtherPayout = (index) => {
        setOtherPayouts(prevPayouts => prevPayouts.filter((_, i) => i !== index));
        setIsEdited(true);
    };

    const handleOtherPayoutChange = (index, field, value) => {
        setOtherPayouts(prevPayouts => {
            const newPayouts = [...prevPayouts];
            newPayouts[index] = {
                ...newPayouts[index],
                [field]: field === 'amount' ? (isNaN(parseFloat(value)) ? 0 : parseFloat(value).toFixed(2)) : value
            };
            return newPayouts;
        });
        setIsEdited(true);
    };

    const handleOtherPayoutBlur = (index, value) => {
        setOtherPayouts(prevPayouts => {
            const newPayouts = [...prevPayouts];
            const parsedValue = parseFloat(value);
            if (!isNaN(parsedValue) && value.trim() !== '') {
                newPayouts[index].amount = parsedValue.toFixed(2);
            }
            return newPayouts;
        });
        setIsEdited(true);
    };

    const handlePayoutSave = async () => {
        try {
            const updatedData = {
                ...payoutData,
                name: payoutName,
                startDate: Math.floor(startDate.unix()),
                endDate: Math.floor(endDate.unix()),
                otherPayouts
            };

            // Update priorities based on the order
            payoutOrder.forEach((key, index) => {
                Object.keys(updatedData.mainPayouts).forEach(groupKey => {
                    if (updatedData.mainPayouts[groupKey].payouts[key]) {
                        updatedData.mainPayouts[groupKey].payouts[key].priority = index;
                    }
                });
            });

            await set(payoutRef, updatedData);
            setIsSaved(true);
            setIsEdited(false);
            setTimeout(() => setIsSaved(false), 1000);
        } catch (error) {
            console.error("Error saving payouts: ", error);
        }
    };


    return (
        <div className="container">
            <Breadcrumbs aria-label="breadcrumb">
                <Link underline="hover" color="inherit" onClick={() => navigate(`/app/league/${leagueId}/dashboard`)}>
                    {leagueData.name}
                </Link>
                <Link underline="hover" color="inherit" onClick={() => navigate(`/app/league/${leagueId}/payouts`)}>
                    Payout Sets
                </Link>
                <Typography sx={{ color: 'text.primary' }}>{payoutName}</Typography>
            </Breadcrumbs>
                <div className={"page-head-div"}>
                <h1>Payouts</h1>
                <Dropdown>
                    <MenuButton variant="plain" sx={{width: "35px", height: "35px"}}><PrintIcon/></MenuButton>
                    <Menu>
                        <MenuItem onClick={
                            () => {
                                setReportModalOpen(true);
                                setReportType('payoutsReport');
                            }}>Payouts Report</MenuItem>
                    </Menu>
                </Dropdown>
            </div>
            <div className="dashboard">
                <h1>Payout Settings</h1>
                <div style={{ display: 'flex', flexDirection: 'column', width: '400px' }}>
                    Name
                    <input className={'styled-select'} value={payoutName} onChange={(e) => {
                        setPayoutName(e.target.value);
                        setIsEdited(true);
                    }} />
                    <div style={{ display: 'flex', flexDirection: 'row', gap: '0px' }}>
                        <div style={{ width: '180px', paddingRight: '40px' }}>
                            Start Date
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker className={'styled-select'} value={startDate} onChange={(date) => {
                                    setStartDate(date);
                                    setIsEdited(true);
                                }} />
                            </LocalizationProvider>
                        </div>
                        <div style={{ width: '180px' }}>
                            End Date
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DateTimePicker className={'styled-select'} value={endDate} onChange={(date) => {
                                    setEndDate(date);
                                    setIsEdited(true);
                                }} />
                            </LocalizationProvider>
                        </div>
                    </div>
                </div>

                <h2>Ordered Payouts</h2>
                {payoutData.mainPayouts && (
                    <div>
                        {Object.entries(payoutData.mainPayouts).map(([groupKey, group]) => (
                            <div key={groupKey} className="payout-group-card">
                                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                                    <input className={'styled-select group-title'}
                                           value={group.name}
                                           onChange={(e) => handleGroupTitleChange(groupKey, e.target.value)}
                                           style={{ width: '400px' }}
                                    />
                                    <Button onClick={() => deletePayoutGroup(groupKey)} className={'button-gradient'} style={{height: '40px'}}><i className={'fi-rr-trash'}></i></Button>
                                </div>
                                {Object.keys(group.payouts || {}).length > 0 ? (
                                    payoutOrder.map((payoutKey, index) => {
                                        const payout = group.payouts[payoutKey];
                                        if (!payout) return null;
                                        return (
                                            <div key={payoutKey} className="ldb-match" style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                                                <div style={{ display: 'flex', flexDirection: 'row', gap: '10px'}}>
                                                    <div style={{marginTop: '0px', display: 'flex', flexDirection: 'column', height: '40px', justifyContent: 'space-between'}}>
                                                        <button
                                                            onClick={() => movePayout(index, -1)}
                                                            disabled={index === 0} className={'hidden-button'}><i className={'fi-br-angle-up'}></i></button>
                                                        <button
                                                            onClick={() => movePayout(index, 1)}
                                                            disabled={index === payoutOrder.length - 1} className={'hidden-button'}><i className={'fi-br-angle-down'}></i></button>
                                                    </div>
                                                    <input className={'styled-select'}
                                                           value={payout.name}
                                                           style={{width: '200px', marginBottom: '0px'}}
                                                           onChange={(e) => handleInputChange(groupKey, payoutKey, 'name', e.target.value)} />
                                                    <input className={'styled-select'}
                                                           value={`$${payout.amount}`}
                                                           style={{width: '100px', marginBottom: '0px'}}
                                                           onChange={(e) => handleInputChange(groupKey, payoutKey, 'amount', e.target.value.replace(/[^0-9.]/g, ''))} />
                                                    <Select className={'styled-select'}
                                                            value={payout.type}
                                                            style={{width: '100px', marginBottom: '0px'}}
                                                            onChange={(e, newValue) => handleInputChange(groupKey, payoutKey, 'type', newValue)}>
                                                        {Object.keys(TYPE_MAP).map(key => (
                                                            <Option key={key} value={key}>{TYPE_MAP[key]}</Option>
                                                        ))}
                                                    </Select>
                                                    <Select className={'styled-select'}
                                                            value={payout.tieBehavior}
                                                            style={{width: '300px', marginBottom: '0px'}}
                                                            onChange={(e, newValue) => handleInputChange(groupKey, payoutKey, 'tieBehavior', newValue)}>
                                                        {Object.keys(TIE_BEHAVIOR_MAP).map(key => (
                                                            <Option key={key} value={key}>{TIE_BEHAVIOR_MAP[key]}</Option>
                                                        ))}
                                                    </Select>
                                                </div>
                                                <Button onClick={() => deletePayout(groupKey, payoutKey)} className={'button-gradient'}><i className={'fi-rr-trash'}></i></Button>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <p>No payouts in this group.</p>
                                )}
                                <Button onClick={() => addPayout(groupKey)} className={'button-gradient'} style={{width: "120px"}}>Add Payout</Button>
                            </div>
                        ))}
                    </div>
                )}
                <Button onClick={addPayoutGroup} className={'button-gradient'}>Add Payout Group</Button>
                <h1>Other Payouts</h1>
                {otherPayouts.map((payout, index) => (
                    <div key={index} className="ldb-match" style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                        <div style={{ display: 'flex', flexDirection: 'row', gap: '10px'}}>
                            <input className={'styled-select'}
                                   value={payout.name}
                                   style={{width: '200px', marginBottom: '0px'}}
                                   onChange={(e) => handleOtherPayoutChange(index, 'name', e.target.value)} />
                            <input className={'styled-select'}
                                   value={`$${payout.amount}`}
                                   style={{width: '100px', marginBottom: '0px'}}
                                   onChange={(e) => handleOtherPayoutChange(index, 'amount', e.target.value.replace(/[^0-9.]/g, ''))}
                                   onBlur={(e) => handleOtherPayoutBlur(index, e.target.value)} />                            <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0px' }}>
                                Hole Bound
                                <input type="checkbox"
                                       checked={payout.holeBound}
                                       onChange={(e) => handleOtherPayoutChange(index, 'holeBound', e.target.checked)} />
                            </label>
                        </div>
                        <Button onClick={() => deleteOtherPayout(index)} className={'button-gradient'}><i className={'fi-rr-trash'}></i></Button>
                    </div>
                ))}
                <Button onClick={addOtherPayout} className={'button-gradient'} style={{width: "170px"}}>Add Other Payout</Button>
                <br/>
                <br/>
                <Button className={'button-gradient'} onClick={handlePayoutSave} disabled={!isEdited}>
                    {isSaved ? 'Saved' : 'Save'}
                </Button>
            </div>
            <QuickReportGenerator
                type={reportType}
                id={""}
                modalOpen={reportModalOpen}
                closeModal={() => setReportModalOpen(false)}
                leagueId={leagueId}
            />
        </div>
    );
};

export default Payout;